import { getPathValue, isRequirementComplete, isUnfilledValue, type RequirementContext } from "./guidance";
import { adapterDefinitions } from "./definitions";
import { resolveAdapterPathSelectors } from "./pathSelectors";
import { parseRecordFieldReference } from "./deltaOutputFields";
import { remapAdapterMappingPath } from "./mappingMerge";
import { getAdapterPersistenceId, getWorkflowResources, MAPPING_RESOURCE_KINDS } from "./helpers";
import { getOwnedResource } from "./runtime/ownership";
import type { AdapterBoundValue } from "./bindings";
import type {
  AdapterMappingDefinition,
  AdapterMappingGuidance,
  ReleaseWithAdapterCollections,
} from "./types";

/** Returns the guidance still requiring configuration for a mapping's current values. */
export const getUnresolvedAdapterMappingGuidance = (
  values: unknown,
  guidance: AdapterMappingGuidance[] | undefined,
  context: RequirementContext = {},
): AdapterMappingGuidance[] =>
  (guidance ?? []).filter((item) => {
    if (item.optional) {
      const targetPath = resolveAdapterPathSelectors(values, item.targetPath, { id: item.id });
      // An optional field that doesn't exist has nothing to resolve, and nor does an optional setting (not a CSV
      // column, which is only listed once added) that is still empty or an unfilled placeholder.
      if (!targetPath || (!item.column && isUnfilledValue(getPathValue(values, targetPath)))) return false;
      // An optional template column the template doesn't read yet is simply not used.
      if (item.requirements.some((requirement) => requirement.operator === "templateColumn")
        && !item.requirements.every((requirement) => isRequirementComplete(values, requirement, item.id, context))) return false;
    }
    return !item.requirements.every((requirement) => isRequirementComplete(values, requirement, item.id, context));
  });

/**
 * Whether `values` satisfy a guided field: its requirements (or the given subset of them) are all met. The
 * adapter's column picker and the mapping workbench's field picker both suggest options with this.
 */
export const satisfiesAdapterMappingGuidance = (
  values: unknown,
  guidance: AdapterMappingGuidance,
  requirements: AdapterMappingGuidance["requirements"] = guidance.requirements,
  context: RequirementContext = {},
): boolean => getUnresolvedAdapterMappingGuidance(values, [{ ...guidance, requirements }], context).length === 0;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const getAdapterMappingColumns = (
  mapping: AdapterMappingDefinition,
  includeOptional = false,
): Record<string, unknown>[] =>
  (mapping.guidance ?? [])
    .filter((item) => item.column && (includeOptional || !item.optional))
    .map((item) => structuredClone(item.column!));

/**
 * A guided date column says which formats the file's dates come in; the mapping's column has to read each of them,
 * or those rows fail to import.
 */
const dateFormatRequirements = (item: AdapterMappingGuidance): AdapterMappingGuidance["requirements"] =>
  item.column?.type === "date" && typeof item.column.dateFormat === "string" && item.column.dateFormat.trim()
    ? [{ path: "columns[id=this.id].dateFormat", operator: "dateFormats", value: item.column.dateFormat, severity: "error" }]
    : [];

/** Aligns adapter guidance with the actual source-column order in a saved mapping. */
export const resolveAdapterMappingGuidance = (
  mapping: AdapterMappingDefinition,
  values: unknown,
): AdapterMappingGuidance[] => {
  const guidance = mapping.guidance ?? [];
  const templateColumns = getAdapterMappingColumns(mapping);
  const templateData = isRecord(mapping.data) && templateColumns.length
    ? { ...mapping.data, columns: templateColumns }
    : mapping.data;
  if (!isRecord(templateData) || !isRecord(values)) return guidance;

  const remapPath = (path: string): string => remapAdapterMappingPath(templateData, values, path) ?? path;
  const columns = Array.isArray(values.columns) ? values.columns : [];
  return guidance.flatMap((item) => {
    if (item.optional && item.column && !columns.some((column) => isRecord(column) && column.id === item.id)) return [];

    const resolvePath = (path: string): string => {
      const selectedPath = resolveAdapterPathSelectors(values, path, { id: item.id });
      if (selectedPath !== path) return selectedPath ?? path;
      return remapPath(path);
    };

    return [{
      ...item,
      targetPath: resolvePath(item.targetPath),
      requirements: [...item.requirements, ...dateFormatRequirements(item)].map((requirement) => ({
        ...requirement,
        path: resolvePath(requirement.path),
      })),
    }];
  });
};

/** Whether a guided field has an unmet requirement that breaks imports (see the requirement's `severity`). */
export const hasAdapterGuidanceError = (
  values: unknown,
  item: AdapterMappingGuidance,
  context: RequirementContext = {},
): boolean =>
  // A column that isn't in the mapping yet is missing, not wrong.
  !isMissingAdapterMappingColumn(item) &&
  item.requirements.some((requirement) =>
    requirement.severity === "error" && !isRequirementComplete(values, requirement, item.id, context));

/** A column-backed guide item without a matching column cannot be visited in the mapping editor yet. */
export const isMissingAdapterMappingColumn = (item: AdapterMappingGuidance): boolean =>
  Boolean(item.column && !/^columns\[\d+\]/.test(item.targetPath));

/** Resolves guidance only when the open mapping is owned by an enabled release adapter. */
export const getAdapterMappingGuidance = (
  release: ReleaseWithAdapterCollections,
  mappingId: string | undefined,
  values?: unknown,
): AdapterMappingGuidance[] => {
  if (!mappingId) return [];

  return (release.adapters ?? []).flatMap((adapter) => {
    if (adapter.format !== 2 || !adapter.enabled) return [];

    const definition = adapterDefinitions.find((candidate) => getAdapterPersistenceId(candidate.id) === adapter.id);
    if (!definition) return [];

    return getWorkflowResources(definition, MAPPING_RESOURCE_KINDS).flatMap(({ step, declaration }) => {
      const mapping = declaration.resource;
      if (getOwnedResource(adapter.ownedResources, step.key, mapping.key)?.resourceId !== mappingId) return [];
      return values === undefined
        ? (mapping.guidance ?? []).filter((item) => !item.optional)
        : resolveAdapterMappingGuidance(mapping, values);
    });
  });
};

const describePath = (path: string): string =>
  path
    .replace(/\[(\d+)\]/g, (_, index: string) => ` ${Number(index) + 1}`)
    .split(".")
    .pop()!
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();

/** A guided field's display name: its label, its column header, or its target path. */
export const getAdapterGuidanceLabel = (item: AdapterMappingGuidance): string =>
  item.label ?? item.column?.header ?? item.targetPath;

/**
 * What is left to do for a guided field, one plain instruction per unmet requirement (empty when it is set up),
 * for the adapter panel and the field tour.
 */
export interface AdapterGuidanceTodoContext extends RequirementContext {
  /** What the mapping's bindings fill in from the answers (see getAdapterBoundValues), to say what to set. */
  boundValues?: Record<string, AdapterBoundValue>;
}

/** A column's setting as the editor names it ("the attribute", "the entity", "the path"), else undefined. */
const describeColumnSetting = (path: string): string | undefined =>
  ({ field: "the attribute", entity: "the entity", path: "the path" } as Record<string, string>)[path.split(".").pop() ?? ""];

export const getAdapterGuidanceTodo = (
  values: unknown,
  item: AdapterMappingGuidance,
  context: AdapterGuidanceTodoContext = {},
): string[] => {
  // A missing column is already shown as Missing; there is nothing more specific to say until it is added.
  if (isMissingAdapterMappingColumn(item)) return [];
  const label = getAdapterGuidanceLabel(item);
  return item.requirements.flatMap((requirement) => {
    if (isRequirementComplete(values, requirement, item.id, context)) return [];
    const path = resolveAdapterPathSelectors(values, requirement.path, { id: item.id }) ?? requirement.path;
    const value = getPathValue(values, path);

    if (requirement.operator === "recordField") {
      if (value === undefined) return [`Add "${path.split(".").pop()}" to each(record) in the data template.`];
      const field = parseRecordFieldReference(value);
      if (field !== undefined && field.trim() !== "") {
        return [`"${field}" is not an attribute of the record; choose one that is.`];
      }
      return [`Choose the record attribute for ${label} in the data template.`];
    }
    if (requirement.operator === "dateFormats") {
      return [`Set its date format to ${requirement.value}, so it reads the file's dates.`];
    }
    if (requirement.operator === "templateColumn") {
      return [`Read {{${requirement.value}}} in the Extension JSON Template.`];
    }
    if (requirement.operator === "equals") {
      return [`Set ${describePath(requirement.path)} to ${JSON.stringify(requirement.value)}.`];
    }
    // Binding paths select columns by ID (`columns[id=x]`); resolved against the mapping they match this one's.
    const bound = Object.entries(context.boundValues ?? {})
      .find(([target]) => resolveAdapterPathSelectors(values, target) === path)?.[1];
    const setting = describeColumnSetting(requirement.path) ?? describePath(requirement.path);
    if (bound && "value" in bound) return [`Set ${setting} to \`${String(bound.value)}\`.`];
    if (bound) {
      return [bound.waitingOn.type === "entity"
        ? `Choose the ${bound.waitingOn.label} entity in the guide, then set ${setting}.`
        : `Answer "${bound.waitingOn.label}" in the guide, then set ${setting}.`];
    }
    if (item.requirements.length === 1) return [`Set ${label}.`];
    return [setting === "the attribute" ? "Choose the attribute." : `Set ${setting}.`];
  });
};
